import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, AuthButton } from '@/components/ui';
import { ScheduleListView } from './components/ScheduleListView';
import { ScheduleDetailDrawer } from './components/ScheduleDetailDrawer';
import { useMaintenanceSchedules, useMaintenanceScheduleStats } from './hooks/useSchedules';
import type { MaintenanceSchedule } from './types/maintenance-schedules.types';

export default function MaintenanceSchedulesPage() {
  const { projectId } = useParams<{ projectId: string }>();
  
  const { data: schedules = [], isLoading } = useMaintenanceSchedules(projectId || '');
  const { data: stats } = useMaintenanceScheduleStats(projectId || '');
  
  const [selectedSchedule, setSelectedSchedule] = useState<MaintenanceSchedule | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'timeline' | 'list'>('list');

  if (!projectId) return null;

  return (
    <PageLayout>
      <PageLayout.Header
        title="Lịch bảo dưỡng"
        breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Lịch' }]}
        description="Lập lịch và điều phối công việc bảo dưỡng theo thời gian, khu vực và tổ đội."
      >
        <div className="flex gap-2">
          <div className="flex bg-slate-100 p-1 rounded-md">
            <button 
              className={`px-3 py-1 text-sm rounded ${viewMode === 'calendar' ? 'bg-white shadow-sm font-medium' : 'text-os-muted'}`}
              onClick={() => setViewMode('calendar')}
            >
              Calendar
            </button>
            <button 
              className={`px-3 py-1 text-sm rounded ${viewMode === 'list' ? 'bg-white shadow-sm font-medium' : 'text-os-muted'}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
          <Button variant="outline" icon="ri-download-line">Xuất lịch</Button>
        </div>
      </PageLayout.Header>

      {/* Context Bar */}
      <div className="flex flex-wrap gap-4 px-4 py-3 bg-white border-b border-slate-200 text-sm">
        <div><span className="text-os-muted">Dự án:</span> <span className="font-medium text-os-text">Vinhomes Grand Park</span></div>
        <div><span className="text-os-muted">Hợp đồng:</span> <span className="font-medium text-os-text">HD-BD-2026-001</span></div>
        <div><span className="text-os-muted">Chu kỳ:</span> <span className="font-medium text-os-text">Tháng 04/2026</span></div>
      </div>

      {/* KPI Board */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 p-4 pb-0">
        <KPICard title="Tổng lịch" value={stats?.total_schedules || 0} icon="ri-calendar-todo-line" />
        <KPICard title="Lịch hôm nay" value={stats?.today_schedules || 0} icon="ri-calendar-event-line" variant="info" />
        <KPICard title="Lịch tuần này" value={stats?.week_schedules || 0} icon="ri-calendar-view" />
        <KPICard title="Lịch trễ" value={stats?.delayed_schedules || 0} icon="ri-error-warning-line" variant="danger" />
        <KPICard title="Chưa tạo WO" value={stats?.missing_wo || 0} icon="ri-file-warning-line" variant="warning" />
        <KPICard title="Tổ quá tải" value={stats?.overloaded_teams || 0} icon="ri-alarm-warning-line" variant="danger" />
        <KPICard title="Tổ thiếu việc" value={stats?.idle_teams || 0} icon="ri-user-unfollow-line" variant="neutral" />
        <KPICard title="Tỷ lệ hoàn thành" value={`${stats?.completion_rate || 0}%`} icon="ri-checkbox-circle-line" variant="success" />
      </div>

      <PageLayout.Toolbar
        primaryAction={
          <AuthButton hasPermission={true} icon="ri-add-line">
            Tạo lịch
          </AuthButton>
        }
      >
        <div className="flex items-center gap-3 w-full md:w-auto">
          <Button variant="outline" icon="ri-robot-line" className="hidden md:flex text-brand-600 border-brand-200 bg-brand-50">Auto Generate</Button>
          <Search placeholder="Tìm theo khu vực, tổ đội..." className="w-full md:w-64" />
          <Filter 
            options={[
              { id: 'team', label: 'Tổ đội', type: 'select', choices: [{value: 't1', label: 'Tổ 01'}] },
              { id: 'status', label: 'Trạng thái', type: 'select', choices: [{value: 'delayed', label: 'Trễ hạn'}] }
            ]} 
            onFilterChange={() => {}}
          />
        </div>
      </PageLayout.Toolbar>

      <PageLayout.Content>
        {viewMode === 'list' && (
          <div className="hidden md:block">
            <ScheduleListView 
              data={schedules} 
              isLoading={isLoading} 
              onRowClick={setSelectedSchedule} 
            />
          </div>
        )}
        
        {viewMode === 'calendar' && (
          <div className="p-8 text-center text-os-muted bg-slate-50 rounded-lg border border-dashed border-slate-200 h-96 flex flex-col items-center justify-center">
             <i className="ri-calendar-2-line text-4xl mb-2 text-slate-300"></i>
             <p>[Calendar View Placeholder]</p>
             <p className="text-sm">Giao diện lịch sẽ được render tại đây với FullCalendar.</p>
          </div>
        )}

        {/* Mobile View Placeholder */}
        <div className="block md:hidden">
           <div className="p-4 text-center text-os-muted bg-slate-50 rounded-lg border border-dashed border-slate-200">
             [Mobile Card List Placeholder]
           </div>
        </div>
      </PageLayout.Content>

      <ScheduleDetailDrawer 
        schedule={selectedSchedule} 
        isOpen={!!selectedSchedule} 
        onClose={() => setSelectedSchedule(null)} 
      />
    </PageLayout>
  );
}
